import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import FeedbackForm from './FeedbackForm';
import useFeedbackForm from '../../../pages/projects/hooks/useFeedbackForm';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import { FormProvider } from 'react-hook-form';
import { ReactComponent as ExpandDiagonalLineIcon } from 'assets/icons/ExpandDiagonalLineIcon.svg';
import { ReactComponent as CollapseDiagonalLineIcon } from 'assets/icons/CollapseDiagonalLineIcon.svg';
import { theme } from 'config/theme';

export const $Dialog = styled(Dialog, { label: 'ActionModal' })(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: '0px 1px 0px #D9DBE9',
    transition: 'width 0.3s',
    maxWidth: 1280,
    width: 620,
  },
  '& .MuiPaper-root': {
    marginBottom: 'unset',
    padding: '16px 0 0 0',
  },
}));

export const $DialogTitle = styled(MuiDialogTitle, { label: 'DialogTitle' })(({ theme }) => ({
  margin: 0,
  padding: '0 24px',
  height: 72,
  lineHeight: '76px',
  color: '#000000',
  fontWeight: 700,
  fontSize: 32,
  '& .MuiIconButton-root:not(.resize)': {
    top: 16,
    right: 12,
    color: theme.palette.custom.lightBlack,
  },
}));

export const $DialogContent = styled(DialogContent, { label: 'DialogContent' })<{ width?: number }>(
  ({ theme, width }) => ({
    transition: 'width 0.3s, padding 0.3s',
    borderTop: `1px solid ${theme.palette.custom.line}`,
    borderBottom: `1px solid ${theme.palette.custom.line}`,
    width: width || '100%',
    overflow: 'auto',
    backgroundColor: 'white !important',
    zIndex: 1,
    padding: '16px 16px 0px 24px',
    maxHeight: '520px',
  }),
);

type FeedbackModalProps = {
  openFeedback: boolean;
  setFeedbackOpen: any;
};

const FeedbackModal: React.FC<FeedbackModalProps> = ({ openFeedback, setFeedbackOpen }) => {
  const { feedbackForm, triggerSubmit, isSubmitting } = useFeedbackForm(closeHandle);
  const [isFullScreen, setFullScreen] = useState(false);

  function closeHandle() {
    setFeedbackOpen(false);
  }

  return (
    <Dialog
      open={openFeedback}
      fullWidth
      fullScreen={isFullScreen}
      sx={{
        '& .MuiPaper-root': {
          transition: '0.2s',
        },
      }}
    >
      <CustomDialogTitle
        onClose={closeHandle}
        id="dialog-title"
        maxWidth={theme.maxWidth}
        margin="0 auto"
        width="100%"
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mr={5}>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: 'black' }}>
            Feedback to BuildWise
          </Typography>
          <IconButton
            onClick={() => setFullScreen((prevState) => !prevState)}
            sx={{ color: 'custom.black' }}
          >
            {isFullScreen ? (
              <CollapseDiagonalLineIcon width={24} />
            ) : (
              <ExpandDiagonalLineIcon width={24} />
            )}
          </IconButton>
        </Box>
      </CustomDialogTitle>
      <DialogContent dividers id="dialog-description" sx={{ p: 0 }}>
        <Box maxWidth={theme.maxWidth} margin="0 auto" width="100%" px={3} py={2}>
          <Typography>
            <strong>To:</strong>&nbsp; support@buildwise.ai
          </Typography>
          <FormProvider {...feedbackForm}>
            <FeedbackForm />
          </FormProvider>
        </Box>
      </DialogContent>
      <DialogActions
        sx={(theme) => ({ padding: 2, maxWidth: theme.maxWidth, margin: '0 auto', width: '100%' })}
      >
        <Button disabled={isSubmitting} onClick={triggerSubmit} variant="contained">
          {isSubmitting ? <CircularProgress sx={{ color: '#000' }} size={20} /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const $LinkText = styled(Typography, { label: 'LinkText' })(({ theme }) => ({
  '&:hover': {
    color: theme.palette.custom.link,
    textDecoration: 'underline',
  },
}));

export default FeedbackModal;
